import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/features/auth/AuthProvider";
import { ThemeSelectionSheet } from "./ThemeSelectionSheet";
import {
  getNextWeekThemePreference,
  setNextWeekThemePreference,
} from "./themePreference";

const DISMISS_KEY = "bc.theme_notification_dismissed_date";

function wasDismissedToday(): boolean {
  try {
    const stored = localStorage.getItem(DISMISS_KEY);
    return stored === new Date().toISOString().slice(0, 10);
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10));
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * Provider that proactively prompts the user to choose a theme for the
 * next weekly batch.
 *
 * Behaviour:
 * - Only fires for authenticated users.
 * - Only active Thursday through Sunday (the generation window for next week).
 * - Shows the ThemeSelectionSheet after a short delay (so the page has
 *   time to paint) when:
 *   1. No theme has been selected for next week
 *   2. The notification hasn't already been shown today
 * - If the user selects a theme: persists it for next week and closes.
 * - If the user dismisses: records today's date so the prompt won't
 *   reappear until tomorrow.
 * - Stored against next Monday's date, so the preference survives the
 *   Monday boundary and is still valid on the new current week.
 */
export function ThemeNotificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const [showSheet, setShowSheet] = useState(false);

  useEffect(() => {
    // Only prompt logged-in users
    if (!user) return;

    // Only fire Thu-Sun (Thu=4, Fri=5, Sat=6, Sun=0)
    const day = new Date().getDay();
    const isGenerationWindow = day === 0 || day >= 4;
    if (!isGenerationWindow) return;

    // Already selected a theme for next week — no need to prompt
    const pref = getNextWeekThemePreference();
    if (pref !== null) return;

    // Already shown today — wait until tomorrow
    if (wasDismissedToday()) return;

    // Short delay so the page renders first
    const timer = setTimeout(() => {
      setShowSheet(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [user]);

  const currentTheme = getNextWeekThemePreference();

  const handleSelect = useCallback((themeId: string | null) => {
    setNextWeekThemePreference(themeId);
    setShowSheet(false);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setShowSheet(open);
    if (!open) {
      // User dismissed or closed — don't show again today
      markDismissed();
    }
  }, []);

  return (
    <>
      {children}
      <ThemeSelectionSheet
        open={showSheet}
        onOpenChange={handleOpenChange}
        onSelect={handleSelect}
        currentTheme={currentTheme}
      />
    </>
  );
}
